<template>
  <div
    v-if="pageLoading"
    class="m-3"
  >
    <placeholder-content :is-load="true" />
  </div>
  <div
    v-else-if="!hasPermission"
    class="m-3"
  >
    <access-denied/>
  </div>
  <div
    v-else
  >
    <div class="d-flex justify-content-between border-bottom mb-3">
      <ul class="nav nav-custom">
        <li class="nav-item">
          <a class="nav-link active">
            {{ $t('All') }}
            <span class="badge rounded-pill bg-primary">
              {{ datatable.total }}
            </span>
          </a>
        </li>
      </ul>
      <column-toggle
        :headers="allHeaders"
        storage-key="product-modifiers.columns"
        v-model="visibleColumns"
      />
    </div>
    <div class="d-flex justify-content-start mx-3 flex-column flex-sm-row">
      <div class="btn-group btn-group-sm me-sm-2 mb-3">
        <button
          type="button"
          class="btn btn-sm btn-primary"
          @click="() => {
            offcanvasFroms.open = true;
            offcanvasFroms.viewOnly = false;
            offcanvasFroms.type = 'CREATE';
          }"
        >
          <i class="fa-solid fa-circle-plus me-2" />
          {{ $t('Create') }}
        </button>
      </div>

      <div class="custom-input-group input-group input-group-sm me-sm-2 mb-3 w-auto">
        <input
          type="search"
          class="form-control"
          :placeholder="$t('search')+'...'"
          aria-label="Search"
          aria-describedby="search-addon"
          v-model.trim="params.search"
          @keyup.enter="fetchData"
        >
        <button
          class="input-group-text"
          type="button"
          @click="fetchData"
        >
          <i class="fas fa-search" />
        </button>
      </div>

      <button
        type="button"
        class="btn btn-sm btn-outline-secondary text-nowrap me-sm-2 mb-3"
        @click="offcanvasFilter.open = true"
      >
        <i class="fa-solid fa-filter me-1" />
        {{ $t('Filters') }}
        <span v-if="activeFilterCount" class="badge bg-primary ms-1 rounded-pill">
          {{ activeFilterCount }}
        </span>
      </button>

      <per-page
        @per-page="setPerPage"
      />
    </div>
    <vue3-easy-data-table
      v-model:server-options="datatable.serverOptions"
      ref="dataTable"
      :headers="visibleHeaders"
      :items="datatable.items"
      :loading="datatable.loading"
      :server-items-length="datatable.total"
      :theme-color="'#515151'"
      :hide-footer="false"
      :hide-rows-per-page="true"
      :empty-message="$t('No Available Data')"
      :table-min-height="null"
      buttons-pagination
      :body-item-class-name="bodyItemClassNameFunction"
      table-class-name='customize-table'
    >
      <template #header-created_by="header">
        <span class="w-100 text-end">
          {{ header.text }}
        </span>
      </template>
      <template #item-name="item">
        {{ $ln(item) }}
      </template>
      <template #item-products="item">
        <span
          v-for="product in item.products.slice(0, 3)"
          :key="product"
          class="badge bg-light text-dark border me-1 mb-1"
        >
          {{ $t(product) }}
        </span>
        <popper
          v-if="item.products.length > 3"
          :placement="'top'"
          :hover="true"
          :arrow="true"
          :disableClickAway="true"
          class="popper-tags border-0 m-0"
        >
          <span class="badge bg-secondary" style="cursor:default">+{{ item.products.length - 3 }} ...</span>
          <template #content>
            <span
              v-for="product in item.products.slice(3)"
              :key="product"
              class="badge bg-light text-dark border me-1 mb-1"
            >
              {{ $t(product) }}
            </span>
          </template>
        </popper>
        <span v-if="!item.products || item.products.length === 0" class="text-muted">-</span>
      </template>
      <template #item-select_type="item">
        {{ $t(item.select_type) }}
      </template>
      <template #item-recipe_behavior="item">
        <span
          :class="['badge', item.recipe_behavior === 'Stack'? 'bg-primary': 'bg-dark']"
        >
          <i :class="['fa-solid' , item.recipe_behavior === 'Stack'? 'fa-cubes': 'fa-chart-pie']" />
          {{ $t(item.recipe_behavior) }}
        </span>
      </template>
      <template #item-required="item">
        <span
          :class="['badge', item.required? 'bg-success': 'bg-secondary']"
        >
          <i :class="['fa-solid' , item.required? 'fa-check': 'fa-xmark']" />
          {{ item.required? $t('Yes'): $t('No') }}
        </span>
      </template>
      <template #item-tags="item">
        <span
          v-for="tag in item.tags.slice(0, 3)"
          :key="tag.name"
          class="badge me-1 mb-1"
          :style="{ color: tag.color, border: `1px solid ${tag.color}`, backgroundColor: 'transparent' }"
        >
          {{ tag.name }}
        </span>
        <popper
          v-if="item.tags.length > 3"
          :placement="'top'"
          :hover="true"
          :arrow="true"
          :disableClickAway="true"
          class="popper-tags border-0 m-0"
        >
          <span class="badge bg-secondary" style="cursor:default">+{{ item.tags.length - 3 }} ...</span>
          <template #content>
            <span
              v-for="tag in item.tags.slice(3)"
              :key="tag.name"
              class="badge me-1 mb-1"
              :style="{ color: tag.color, border: `1px solid ${tag.color}`, backgroundColor: 'transparent' }"
            >
              {{ tag.name }}
            </span>
          </template>
        </popper>
        <span v-if="!item.tags || item.tags.length === 0" class="text-muted">-</span>
      </template>
      <template #item-out_of_stock_branches="item">
        <div
          v-if="item.out_of_stock_branches && item.out_of_stock_branches.length"
          class="d-flex flex-wrap gap-1"
        >
          <span
            v-for="branch in item.out_of_stock_branches"
            :key="branch.branch_id"
            class="badge bg-danger-subtle text-danger border border-danger-subtle"
          >
            <i class="fa-solid fa-store me-1" />
            {{ branch.name }}
          </span>
        </div>
        <span v-else class="text-muted">-</span>
      </template>
      <template #item-inactive_branches="item">
        <div
          v-if="item.inactive_branches && item.inactive_branches.length"
          class="d-flex flex-wrap gap-1"
        >
          <span
            v-for="branch in item.inactive_branches"
            :key="branch.branch_id"
            class="badge bg-secondary-subtle text-secondary border border-secondary-subtle"
          >
            <i class="fa-solid fa-store me-1" />
            {{ branch.name }}
          </span>
        </div>
        <span v-else class="text-muted">-</span>
      </template>
      <template #item-created_at="item">
        {{ formatDateTime(item.created_at) }}
      </template>
      <template #item-action="item">
        <div class="icon-link custom-actions">
          <popper
            :placement="'top'"
            :hover="true"
            :arrow="true"
            :disableClickAway="true"
            class="popper-default border-0 m-0"
          >
            <button
              type="button"
              data-bs-toggle="popover"
              data-bs-placement="top"
              data-bs-title="Custom popover"
              data-bs-content="This popover is themed via CSS variables."
              class="btn btn-outline-primary btn-sm btn-icon"
              @click="() => {
                formInfo.open = true;
                formInfo.id = item.id;
              }"
            >
              <i class="fa-solid far fa-eye" />
            </button>
            <template #content>
              <div>{{ $t('View') }}</div>
            </template>
          </popper>
          <popper
            :placement="'top'"
            :hover="true"
            :arrow="true"
            :disableClickAway="true"
            class="popper-default border-0 m-0"
          >
            <button
              type="button"
              data-bs-toggle="popover"
              data-bs-placement="top"
              data-bs-title="Custom popover"
              data-bs-content="This popover is themed via CSS variables."
              class="btn btn-outline-primary btn-sm btn-icon"
              @click="() => {
                offcanvasFroms.open = true;
                offcanvasFroms.id = item.id;
                offcanvasFroms.type = 'EDIT';
              }"
            >
              <i class="fa-solid fa-square-pen" />
            </button>
            <template #content>
              <div>{{ $t('Edit') }}</div>
            </template>
          </popper>
          <popper
            :placement="'top'"
            :hover="true"
            :arrow="true"
            :disableClickAway="true"
            class="popper-default border-0 m-0"
          >
            <button
              type="button"
              class="btn btn-outline-primary btn-sm btn-icon"
              @click="manageBranches(item.id)"
            >
              <i class="fa-solid fa-code-branch" />
            </button>
            <template #content>
              <div>{{ $t('Manage Branches') }}</div>
            </template>
          </popper>
          <popper
            :placement="'top'"
            :hover="true"
            :arrow="true"
            :disableClickAway="true"
            class="popper-default border-0 m-0"
          >
            <button
              type="button"
              data-bs-toggle="popover"
              data-bs-placement="top"
              class="btn btn-outline-primary btn-sm btn-icon"
              @click="handleDelete(item.id)"
            >
              <i class="fa-solid fa-trash-can" />
            </button>
            <template #content>
              <div>{{ $t('Delete') }}</div>
            </template>
          </popper>
        </div>
      </template>
      <template #loading>
        <i class="fa-solid fa-spinner fa-spin fa-2xl" />
      </template>
    </vue3-easy-data-table>
    <offcanvas-form
      v-if="offcanvasFroms.open"
      :type="offcanvasFroms.type"
      :id="offcanvasFroms.id"
    />
    <offcanvas-info
      v-if="formInfo.open"
      :id="formInfo.id"
    />
    <offcanvas-manage-branches
      v-if="manageBranchesForm.open"
      :id="manageBranchesForm.id"
      @close="manageBranchesForm.open = false"
    />
    <offcanvas-filter
      v-if="offcanvasFilter.open"
      :sections="filterSections"
      :value="{ branch_ids: params.branch_ids, stock_status: params.stock_status, active_status: params.active_status }"
      @apply="onApplyFilter"
      @close="offcanvasFilter.open = false"
    />
  </div>
</template>
<script lang="js">
import Vue3EasyDataTable from 'vue3-easy-data-table';
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';
import AccessDenied from "@/plugins/AccessDenied.vue";
import PerPage from '@/plugins/PerPage.vue';
import FieldsDef from "./assets/template/datatable/FieldsDef";
import OffcanvasForm from './OffcanvasForm.vue';
import ColumnToggle from '@/components/shared/ColumnToggle.vue';
import { loadVisibleColumns } from '@/utils/columnToggle';
import { Modifiers } from '@/services/endpoint/modifiers.service';
import moment from "moment";
import numeral from 'numeral';
import Popper from "vue3-popper";
import OffcanvasInfo from './OffcanvasInfo.vue';
import OffcanvasManageBranches from './OffcanvasManageBranches.vue';
import OffcanvasFilter from '@/components/shared/OffcanvasFilter.vue';

export default {
  data() {
    return {
      params: {
        limit: 10,
        page: 1,
        search: '',
        branch_ids: [],
        stock_status: [],
        active_status: []
      },
      filters: {
        branches: []
      },
      offcanvasFilter: {
        open: false
      },
      formInfo: {
        open: false,
        id: 0,
      },
      manageBranchesForm: {
        open: false,
        id: 0,
      },
      pageLoading: false,
      hasPermission: false,
      datatable: {
        loading: false,
        headers: FieldsDef,
        items: [],
        total: 0,
        serverOptions: {
          page: 1,
          rowsPerPage: 10
        }
      },
      offcanvasFroms: {
        open: false,
        type: 'CREATE',
        customerId: 0,
        viewOnly: false
      },
      visibleColumns: loadVisibleColumns('product-modifiers.columns', FieldsDef(t => t))
    }
  },
  components: {
    PlaceholderContent,
    AccessDenied,
    PerPage,
    Vue3EasyDataTable,
    OffcanvasForm,
    ColumnToggle,
    Popper,
    OffcanvasInfo,
    OffcanvasManageBranches,
    OffcanvasFilter
  },
  computed: {
    activeFilterCount() {
      return this.params.branch_ids.length + this.params.stock_status.length + this.params.active_status.length;
    },
    // พฤติกรรมสูตร (stack / split_portion) มีผลกับการตัดสต็อกเท่านั้น — แพ็กไม่มี feature_inventory ไม่ต้องมีคอลัมน์นี้
    allHeaders() {
      const headers = this.datatable.headers(this.$t);
      return this.$hasFeature('feature_inventory') ? headers : headers.filter(h => h.value !== 'recipe_behavior');
    },
    visibleHeaders() {
      return this.allHeaders.filter(h => !h.togglable || this.visibleColumns.includes(h.value));
    },
    filterSections() {
      return [
        {
          key: 'branch_ids',
          label: 'Branch',
          icon: 'fa-store',
          options: this.filters.branches
        },
        {
          key: 'stock_status',
          label: 'Stock Status',
          icon: 'fa-box',
          single: true,
          options: [
            { id: 'out_of_stock', name: 'Out of Stock' },
            { id: 'in_stock', name: 'In Stock' }
          ]
        },
        {
          key: 'active_status',
          label: 'Active Status',
          icon: 'fa-toggle-on',
          single: true,
          options: [
            { id: 'active', name: 'Active' },
            { id: 'inactive', name: 'Inactive' }
          ]
        }
      ];
    }
  },
  async mounted() {
    this.pageLoading = true;
    await this.fetchData();
    this.pageLoading = false;
  },
  methods: {
    bodyItemClassNameFunction(column, rowNumber) {
      let className = 'align-top py-1 px-2';
      const columnCenter = [];
      const columnEnd = ['options_count', 'created_by'];
      if (columnCenter.includes(column)) {
        className += ' text-center';
      }
      if (columnEnd.includes(column)) {
        className += ' text-end';
      }
      return className;
    },
    async fetchData() {
      this.datatable.loading = true;
      const { stock_status, active_status, ...requestParams } = this.params;
      // BE expects `is_in_stock` / `is_active` as 1/0; only send when a status is selected
      if (stock_status.length) {
        requestParams.is_in_stock = stock_status[0] === 'in_stock' ? 1 : 0;
      }
      if (active_status.length) {
        requestParams.is_active = active_status[0] === 'active' ? 1 : 0;
      }
      var queryString = '?' + $.param(requestParams);
      await Modifiers.list(queryString).then(res => {
        this.datatable.items = res.data.data;
        this.datatable.total = res.data.meta.total;
        this.filters.branches = res.data.filter?.branches || [];
        this.hasPermission = true;
      }).catch(err => {
        if (err?.response?.status === 403) {
          this.hasPermission = false;
        }
      });

      this.datatable.loading = false;
      return;
    },
    onApplyFilter(payload) {
      this.params.branch_ids = payload.branch_ids || [];
      this.params.stock_status = payload.stock_status || [];
      this.params.active_status = payload.active_status || [];
      this.params.page = 1;
      this.datatable.serverOptions.page = 1;
      this.fetchData();
    },
    setPerPage(perPage) {
      this.params.limit = perPage;
      this.$refs.dataTable.updateRowsPerPageActiveOption(perPage);
      this.fetchData();
    },
    formatDateTime(value) {
      return moment(value).format('YYYY-MM-DD HH:mm:ss');
    },
    formatNumberDigit(value) {
      return numeral(value).format("0,0.00");
    },
    formatNumber(value) {
      return numeral(value).format("0,0");
    },
    manageBranches(id) {
      this.manageBranchesForm.id = id;
      this.manageBranchesForm.open = true;
    },
    async handleDelete(id) {
      try {
        let self = this;
        let title = this.$t("Delete this Modifier group") + '?';
        let html = this.$t("You won't be able to revert this! This group will be unlinked from all active products.");
        let confirmButtonText =
          '<i class="fas fa-check-circle me-1"></i>' + this.$t("Confirm");
        this.$swal({
          icon: "warning",
          iconColor: "#FF7043",
          title: title,
          // text: "You will not be able to undo this action.",
          html: html,
          showCancelButton: true,
          cancelButtonText: this.$t("Cancel"),
          confirmButtonText: confirmButtonText,
          buttonsStyling: false,
          customClass: {
            confirmButton: "btn btn-primary me-2",
            cancelButton: "btn btn-danger",
          },
          focusConfirm: false,
          showLoaderOnConfirm: true,
          preConfirm: async function () {
            return Modifiers.delete(id)
              .then((response) => {
                self.fetchData();
                self.$toast.success("<B>" + self.$t('Success') + "</B>\n" + self.$t('Delete Success!'));
                // $(self.$refs.offcanvaInfo).offcanvas('hide');
              })
              .catch((error) => {
                if (error?.response?.status === 403) {
                  self.$swal.update({
                    title: self.$t('Access Denied'),
                    confirmButtonText: '<i class="fas fa-redo me-1"></i>' + self.$t("Try again"),
                    text: self.$t('You do not have permission to access this page.')
                  });
                  self.$swal.showValidationMessage(
                    self.$t(error?.response?.data?.message)
                  )
                } else if (error?.response?.data?.code === "VALIDATION") {
                  self.$swal.showValidationMessage(
                    self.$t(error?.response?.data?.message)
                  )
                } else {
                  self.$toast.warn(error?.response?.data?.message);
                }
              })
          },
        });
      } catch (error) {
      }
    },
  },
  watch: {
    "datatable.serverOptions" (nval, oval) {
      delete this.params.sort;
      this.params.page = nval.page;
      this.params.limit = nval.rowsPerPage;
      if (nval.sortBy && nval.sortType) this.params.sort = nval.sortBy + "|" + nval.sortType;
      this.fetchData();
    }
  }
}
</script>
